import {
  Component,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
  SimpleChanges,
  ViewChild,
  Input,
  ViewEncapsulation,
  TemplateRef,
  EventEmitter,
  Output,
  AfterViewInit,
} from '@angular/core';
import { NotifierService } from 'angular-notifier';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { InboundOrderDto } from 'src/app/domain/models/dto/inbound-order.dto';
import { BoobleModalService } from 'booble-modal';
import { SendDocumentsModalComponent } from '../modals/send-documents-modal/send-documents-modal.component';
import { OrderDetailsModalComponent } from '../modals/order-details-modal/order-details-modal.component';
import { ActivatedRoute } from '@angular/router';
import { AttachInvoiceModalComponent } from '../modals/attach-invoice-modal/attach-invoice-modal.component';
import { QuotationModalComponent } from '../modals/quotation-modal/quotation-modal.component';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { Moedas } from 'src/app/domain/constants/moeda.constant';
import { OrderPaymentComponent } from '../../../outbound/components/modals/order-payment/order-payment.component';
import { CountdownEvent } from 'ngx-countdown';
import { BankChannelModalComponent } from '../modals/bank-channel-modal/bank-channel-modal.component';
import { UpdateOrderComponent } from 'src/app/shared/modals/update-order/update-order.component';
import { OrderPaymentInboundComponent } from '../../../inbound/components/modals/order-payment-inbound/order-payment-inbound.component';
import * as saveAs from 'file-saver';
import { catchError, forkJoin } from 'rxjs';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { QuotationModalMobileComponent } from '../modals/quotation-modal/quotation-modal-mobile/quotation-modal-mobile.component';
import { GenericBottomSheetComponent } from 'src/app/shared/components/generic-bottom-sheet/generic-bottom-sheet.component';
import { SelectBankAccountComponent } from '../../../inbound/components/modals/select-bank-account/select-bank-account.component';
import { ResponseTableModel } from 'src/app/domain/models/response-table.model';

@Component({
  selector: 'app-table-component',
  templateUrl: 'table.component.html',
  styleUrls: ['table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit {
  isPopoverOpen: boolean = false;

  popoverStates: { [key: number]: boolean } = {};

  tenant: string = '';
  rType: string = '';
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @Input() data: ResponseTableModel<InboundOrderDto[]>;
  @Input() displayedColumns: string[];
  private readonly notifier: NotifierService;
  moedas = Moedas;
  isMobile: boolean;

  selection = new SelectionModel<any>(true, []);
  public dataSource: MatTableDataSource<InboundOrderDto>;
  @Input() pageNumber: number;
  @Input() pageSize: number;
  @Input() totalElements: number;

  @Output() pageChange = new EventEmitter<{
    pageNumber: number;
    pageSize: number;
  }>();

  @ViewChild('bottomSheetContent') bottomSheetContent: TemplateRef<any>;

  constructor(
    private modalService: BoobleModalService,
    private ordersService: OrdersService,
    private bottomSheet: MatBottomSheet,
    notifierService: NotifierService,
    private readonly route: ActivatedRoute
  ) {
    this.notifier = notifierService;
    this.route.paramMap.subscribe((params) => {
      const result = params.get('tenant');
      const rType = params.get('rType');

      if (result) this.tenant = params.get('tenant');
      if (rType) this.rType = params.get('rType');
    });

    if (this.rType == '0')
      this.displayedColumns = [
        'id',
        'createdDate',
        'companyName',
        'amount',
        'status',
        'action',
      ];

    if (this.rType == '1')
      this.displayedColumns = [
        'id',
        'createdDate',
        'outReceiverName',
        'amount',
        'status',
        'action',
      ];
  }

  public ngOnInit() {
    this.isMobile = window.innerWidth <= 820;
    this.route.paramMap.subscribe((params) => {
      const rType = params.get('rType');

      if (rType) this.rType = params.get('rType');
    });
    this.initializeTable();
  }

  ngAfterViewInit(): void {
    this.initializeTableSort();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.updateTableData(changes['data'].currentValue);
      this.initializeTableSort();
    }
  }

  initializeTableSort() {
    if (this.sort) {
      this.sort.active = 'id';
      this.sort.direction = 'desc';
      this.dataSource.sort = this.sort;
    }

    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'branch':
          return item.receiver.branch;
        case 'companyName':
          return item.sender.companyName;
        case 'status':
          return item.statusFlow;
        case 'outReceiverDocument':
          return item.receiver.documentNumber;
        case 'outReceiverName':
          return item.receiver.name;
        default:
          return item[property] || '';
      }
    };
  }

  private initializeTable() {
    this.dataSource = new MatTableDataSource(this.data?.content || []);
    this.dataSource.paginator = this.paginator;

    this.paginator._intl.getRangeLabel = (
      page: number,
      pageSize: number,
      length: number
    ) => {
      const startIndex = page * pageSize + 1;
      const endIndex = Math.min(startIndex + pageSize - 1, length);
      return `Exibindo ${endIndex} de ${length} ordens`;
    };
  }

  private updateTableData(data: ResponseTableModel<InboundOrderDto[]>) {
    this.dataSource = new MatTableDataSource(data?.content || []);
    this.totalElements = data?.totalElements || 0;

    if (this.paginator) {
      this.paginator.pageIndex = this.pageNumber;
      this.paginator.pageSize = this.pageSize;
      this.paginator.length = this.totalElements;
    }
  }

  onPageChange(event: { pageIndex: number; pageSize: number }) {
    const { pageIndex, pageSize } = event;

    sessionStorage.setItem('orders-page-size', pageSize.toString());
    sessionStorage.setItem('orders-page-number', pageIndex.toString());

    this.pageChange.emit({
      pageNumber: pageIndex,
      pageSize,
    });
  }

  openBottomSheet() {
    this.bottomSheet.open(GenericBottomSheetComponent, {
      data: { content: this.bottomSheetContent },
    });
  }

  togglePopover(rowId: number) {
    this.popoverStates[rowId] = !this.popoverStates[rowId];
  }

  onRowToggled(row: any) {
    this.selection.toggle(row);
  }

  isAllSelected() {
    return this.selection.selected?.length == this.dataSource.data.length;
  }

  calcCountdown(dueDate: string): number {
    var now = new Date();
    var countDownDate = new Date(dueDate);
    var dif = countDownDate.getTime() - now.getTime();

    if (dif < 0) {
      var Seconds_Between_Dates = 0;
    } else {
      var Seconds_from_T1_to_T2 = dif / 1000;
      var Seconds_Between_Dates: number = Math.abs(Seconds_from_T1_to_T2);
    }
    return Seconds_Between_Dates;
  }

  downloadAttachment(fileName: string) {
    this.ordersService.downloadOrderAttachmentByName(fileName).subscribe({
      next: (data: any) => {
        saveAs(data, fileName);
        this.notifier.notify(
          'success',
          'Download do arquivo realizado com sucesso.'
        );
      },
      error: (err) => {
        this.notifier.notify('error', 'Erro ao efetuar o download do arquivo.');
      },
    });
  }

  toggleAll() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.selection.select(...this.dataSource.data);
    }
  }

  openSendDocumentsModal(orderId: number) {
    this.modalService.present(SendDocumentsModalComponent, orderId);
  }

  openQuotationModal(order: InboundOrderDto) {
    if (!this.isMobile) {
      const data = {
        order: order,
        orderType: order.remmitanceType === 'OUTBOUND' ? 1 : 0,
        finality: order.finality,
        amount: order.amount,
        country:
          order.remmitanceType === 'OUTBOUND'
            ? order.receiver.country
            : order.sender.country,
        orderId: order.id,
        currency: this.moedas.find((moeda) => moeda.id === order.currency.id),
      };
      this.modalService.present(QuotationModalComponent, data);
    } else {
      const bottomSheetConfig = {
        data: {
          order: order,
          orderType: order.remmitanceType === 'OUTBOUND' ? 1 : 0,
          finality: order.finality,
          amount: order.amount,
          country:
            order.remmitanceType === 'OUTBOUND'
              ? order.receiver.country
              : order.sender.country,
          orderId: order.id,
          currency: this.moedas.find((moeda) => moeda.id === order.currency.id),
        },
      };
      this.bottomSheet.open(QuotationModalMobileComponent, bottomSheetConfig);
    }
  }

  onCountdownFinished(e: CountdownEvent) {
    if (e.action == 'done') {
      this.ordersService.updateOrderView();
    }
  }

  openOrderPaymentModal(order: InboundOrderDto) {
    if (this.isMobile) {
      this.openBottomSheet();
    } else {
      this.modalService.present(OrderPaymentComponent, order?.id);
    }
  }

  openAttachInvoiceModal(order: InboundOrderDto) {
    this.modalService.present(AttachInvoiceModalComponent, order);
  }

  openSendDocumentsReviewModal(order: InboundOrderDto) {
    this.modalService.present(SendDocumentsModalComponent, order);
  }

  openOrderDetailsModal(order: InboundOrderDto) {
    this.modalService.present(OrderDetailsModalComponent, order);
  }

  openUpdateOrderModal(order: InboundOrderDto) {
    this.modalService.present(UpdateOrderComponent, order);
  }

  openPaymentInboundModal(order: InboundOrderDto) {
    this.modalService.present(OrderPaymentInboundComponent, order);
  }
  openSelectBankAccount(order: InboundOrderDto) {
    this.modalService.present(SelectBankAccountComponent, order);
  }

  openBankChannelModal(currencyDescription: string) {
    this.modalService.present(BankChannelModalComponent, currencyDescription);
  }
}
